import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormsModule } from '@angular/forms';

import { DefaultIcon, SDate } from '@summit/core';
import {
  DateRange,
  DateRangeType,
  IUserExperience,
  SummitIdObject,
  SummitUserExperienceService,
  Week
} from '@summit/models';

import { AsyncPipe } from '@angular/common';
import { LayoutService } from '@summit/ui';
import { DatePicker } from 'primeng/datepicker';
import { FloatLabel } from 'primeng/floatlabel';
import { SummitControl } from '../../models';

@Component({
  selector: 's-form-input-date-range',
  templateUrl: './s-form-input-date-range.component.html',
  styleUrls: ['./s-form-input-date-range.component.scss'],
  standalone: true,
  imports: [FormsModule, FloatLabel, DatePicker, AsyncPipe]
})
export class SFormInputDateRangeComponent<T extends SummitIdObject> extends SummitControl<T> implements OnInit {
  @Input() defaultWeekOffset = 0;
  @Input() defaultRange: DateRange;
  @Input() defaultRangeType: DateRangeType = DateRangeType.TenantWeek;
  @ViewChild('calendar') calendar: DatePicker;
  @Output()
  DateRangeChange: EventEmitter<DateRange> = new EventEmitter<DateRange>();
  private dateRangeFocus = false;
  public controlValue?: Date[];
  public rangeValue: DateRange;
  private calendarVisible = false;
  defaultIcon = DefaultIcon;

  constructor(
    fb: FormBuilder,
    private uxService: SummitUserExperienceService<IUserExperience>,
    protected layoutService: LayoutService
  ) {
    super();
  }

  override ngOnInit() {
    /*
    console.log(
      `[RangePicker] Initializing with ${this.defaultRange?.begin?.format('MM/DD')} & ${this.defaultRange?.end?.format(
        'MM/DD'
      )}`,
      this.defaultRange,
      this.defaultRangeType
    );
    */
    this.defaultRangeType = this.defaultRangeType ?? DateRangeType.TenantWeek;
    if (this.defaultRange) {
      //console.log(`[RangePicker] Initializing with ${this.defaultRange?.begin?.format('MM/DD')}`);
      this.updateRange(this.defaultRange);
    } else {
      const selWeek = new Week(this.defaultWeekOffset, this.defaultRangeType);
      //console.log(`[RangePicker] Initializing with current month`);
      this.updateRange(
        new DateRange(
          {
            begin: selWeek.start,
            end: selWeek.end
          },
          this.defaultRangeType
        )
      );
    }
    this.updateRange;
  }

  public onShow() {
    this.calendarVisible = true;
  }
  public onClose() {
    this.calendarVisible = false;
  }

  public ProcessRangeChange() {
    const beginDate = SDate.ShiftTZToUTC(this.rangeValue.begin) ?? this.rangeValue.begin?.toDate?.();
    const endInput = this.rangeValue.end ?? this.rangeValue.begin;
    const endDate = SDate.ShiftTZToUTC(endInput) ?? endInput?.toDate?.();
    const begin = SDate.newUtc(beginDate ?? undefined);
    const end = SDate.newUtc(endDate ?? undefined).endOf('month');
    // console.log(`[RangePicker] Processing Range Change: ${begin.format('MM/DD/YYYY')} - ${end.format('MM/DD/YYYY')}`);
    this.updateRange(new DateRange({ begin: begin, end: end }));
  }

  private getDefaultDateRange(): DateRange {
    return this.defaultRange ?? new Week(0, this.defaultRangeType).ToDateRange(this.defaultRangeType);
  }

  updateRange(newRange?: DateRange) {
    newRange = newRange ? newRange : this.getDefaultDateRange();
    if (newRange.end.date() != newRange.end.daysInMonth()) {
      newRange.end = newRange.end.endOf('month');
    }
    if (!this.rangeValue || !newRange.end.isSame(this.rangeValue.end)) {
      const begin = SDate.ShiftTZToLocal(newRange.begin);
      const end = SDate.ShiftTZToLocal(newRange.end);
      //console.log(`[RangePicker] Updating Range: ${begin} - ${end}`);
      this.controlValue = begin && end ? [begin, end] : undefined;
      this.rangeValue = newRange;
      // console.log(
      //   `[RangePicker] Updating Range: ${this.rangeValue.begin.format('MM/DD/YYYY')} - ${this.rangeValue.end.format(
      //     'MM/DD/YYYY'
      //   )}`
      // );
      this.DateRangeChange.emit(new DateRange(this.rangeValue));
    }
  }
  moveToToday() {
    const newRange = new DateRange(this.defaultRangeType);
    // console.log(`Moving to Today: ${newRange.begin.format()}-${newRange.end.format()}`);
    this.updateRange(newRange);
  }

  moveBackWeek() {
    // Enter key in input is triggering this so added a if to separate decision
    if (!this.dateRangeFocus) {
      const currentValue = new DateRange(this.rangeValue);
      currentValue.begin = currentValue.begin.add(-1, 'month');
      currentValue.end = currentValue.end.add(-1, 'month');
      this.updateRange(currentValue);
    } else {
      this.ProcessRangeChange();
    }
  }

  moveForwardWeek() {
    //console.log('Forward a week');
    const currentValue = new DateRange(this.rangeValue);
    currentValue.begin = currentValue.begin.add(1, 'month');
    currentValue.end = currentValue.end.add(1, 'month');
    this.updateRange(currentValue);
  }

  getRange(): DateRange {
    return new DateRange(this.rangeValue);
  }
  /*
  setFocusTracking(newFocusState: boolean) {
    if (!newFocusState) {
      //console.log(`Focus changed calling ProcessRange`);
      this.ProcessRangeChange();
    }
    this.dateRangeFocus = newFocusState;
  }
*/
  onChange() {
    //console.log(`${this.fieldName.toString()}: onChange`, this.controlValue);
    if (!this.controlValue || !this.controlValue[1]) return;
    this.rangeValue = new DateRange({
      begin: SDate.newUtc(this.controlValue?.[0]),
      end: SDate.newUtc(this.controlValue?.[1])
    });
    //console.log(`${this.fieldName.toString()}: onChange`, this.rangeValue);
    this.ProcessRangeChange();
    if (this.calendarVisible) {
      this.calendarVisible = false;
      this.calendar?.toggle();
    }
  }
}
